import React, {useEffect, useMemo, useState} from 'react';
import {Alert, Button, Divider, Empty, InputNumber, Menu, Popover, Select, Spin, Table, Tag, Tooltip, message,} from 'antd';
import {CaretRightOutlined, ForwardOutlined, ToolOutlined, UnorderedListOutlined,} from '@ant-design/icons';
import {AppActions, useAppContext} from '../../context/AppContext';
import {useUser} from '../../context/UserContext';
import ApiService from '../../services/ApiService';
import SelectPlan from '../SelectPlan';
import SelectionInfo from './SelectionInfo';
import PopoverScrollContent from '../../components/PopoverScrollContent';
import {v4} from 'uuid';
import ProjectMD5 from '../../helpers/ProjectMD5';
import {FIXES, getFix, scanHistoryForFix} from './fix/fixRegistry';

// Fix Module shell — registry-driven (bkz. fix/fixRegistry.js).
//  - Check'i ASLA tekrar kosmaz: bulgulari mevcut Check geçmişinden okur.
//  - Secim/parametre state'i fix bazinda GLOBAL'dir (sekmeler arasi kalici).
//  - Tek GLOBAL Apply tum sekmelerdeki secimi topluca uygular -> TEK duplicate.
//  - Batch Fix All bagimsizdir: mevcut TUM Check sonuclarina otomatik uygular.
export default function FixModule({initialFixId = null, presetCrossSection = null}) {
    const {appContext, dispatchProject, resolveSelection, resolveHistorySelection, selection: selectionRef} = useAppContext();
    const {user, settings} = useUser();
    const selection = resolveSelection(selectionRef) || {base: {}, compare: {}};

    const [activeKey, setActiveKey] = useState(initialFixId || 'overview'); // 'overview' | fixId
    const [fixData, setFixData] = useState({}); // { [fixId]: { rows:[{key,fixable,raw,params}], selected:[key] } }
    const [duplicateId, setDuplicateId] = useState(null);
    const [changes, setChanges] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusText, setStatusText] = useState('');
    // Son apply kendi uzerine yazma miydi? (true -> sadece secim + applied ozeti goster)
    const [selfApplied, setSelfApplied] = useState(false);

    // ---- parse-internal ile ayni input sekli ----
    const getInputData = (side) => {
        const hasProjProjection = !!side?.project?.projection?.text;
        return {
            project: side?.project?.information,
            projection: hasProjProjection ? side.project.projection : null,
            geometry: side?.plan?.geometry,
            flow: side?.plan?.flow,
            output: side?.plan?.mappedProfiles ?? [],
            plan: side?.plan?.plan,
            type: side?.plan?.type,
            profileNames: (side?.plan?.mapProfiles || []).map((x) => {
                if (!x.targetName) x.targetName = x.target;
                return x;
            }),
            settings: user.settings,
            projectHash: side?.plan?.projectHash,
            river: side?.river?.river || null,
            reach: side?.river?.reach || null,
        };
    };

    const historyIdFor = (side) => `${side?.plan?.id}_${side?.river?.key}`;

    // ---- secili plan/river'in Check geçmişi ----
    const history = useMemo(() => {
        if (!selection?.base?.plan) return null;
        const id = historyIdFor(selection.base);
        return appContext?.history?.find((x) => x.id === id && x.type === 'Check');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection?.base?.plan?.id, selection?.base?.river?.key, appContext?.history]);

    // ---- bu geçmişte hangi fix'ler mevcut (bulgu var) ----
    const available = useMemo(
        () => FIXES.map((def) => scanHistoryForFix(history, def)).filter(Boolean),
        [history]
    );

    // ---- fix bazinda satir + secim state'ini kur (gecmis/secim degisince) ----
    useEffect(() => {
        const next = {};
        for (const scan of available) {
            const rows = scan.items.map((it) => ({
                key: scan.def.rowKey(it),
                fixable: scan.def.isFixable(it),
                raw: it,
                // defaultParams ikinci arg olarak ham bulguyu alir (satir-bazli
                // varsayilan gerekiren fix'ler icin, orn. XY06 measured skew).
                params: scan.def.defaultParams(settings, it),
            }));
            // varsayilan: fixlenebilir olanlar staged (presetCrossSection varsa sadece o)
            const selected = rows
                .filter((r) => r.fixable && (!presetCrossSection || String(r.raw.cross_section) === String(presetCrossSection)))
                .map((r) => r.key);
            next[scan.def.id] = {rows, selected};
        }
        setFixData(next);
        // Tekrar Check calistirilip bulgu geldiyse minimal gorunumden cik.
        if (available.length > 0) setSelfApplied(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [available]);

    // Plan/river degisince minimal gorunumu ve applied ozetini sifirla.
    useEffect(() => {
        setSelfApplied(false);
        setChanges([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection?.base?.plan?.id, selection?.base?.river?.key]);

    const updateParam = (fixId, rowKey, field, value) => {
        setFixData((prev) => {
            const fd = prev[fixId];
            if (!fd) return prev;
            return {
                ...prev,
                [fixId]: {
                    ...fd,
                    rows: fd.rows.map((r) => (r.key === rowKey ? {...r, params: {...r.params, [field]: value}} : r)),
                },
            };
        });
    };

    const setSelected = (fixId, keys) => {
        setFixData((prev) => (prev[fixId] ? {...prev, [fixId]: {...prev[fixId], selected: keys}} : prev));
    };

    // ---- staged: tum sekmelerde secili (uygulanacak) satirlar ----
    const staged = useMemo(() => {
        const list = [];
        for (const scan of available) {
            const fd = fixData[scan.def.id];
            if (!fd) continue;
            for (const r of fd.rows) {
                if (fd.selected.includes(r.key)) list.push({def: scan.def, row: r});
            }
        }
        return list;
    }, [available, fixData]);

    // ---- overview: XS bazinda uygulanacak fix'ler (staged'in onizlemesi) ----
    const stagedByRs = useMemo(() => {
        const byRs = {};
        for (const {def, row} of staged) {
            const rs = row.raw.cross_section;
            if (!byRs[rs]) byRs[rs] = {key: String(rs), rs, type: row.raw.type, defs: []};
            // Bir fix tipi ayni RS altinda birden cok satira sahip olabilir (orn. PS00
            // her tolerans alani icin); overview'da tek etiket goster.
            if (!byRs[rs].defs.includes(def)) byRs[rs].defs.push(def);
            if (!byRs[rs].type && row.raw.type) byRs[rs].type = row.raw.type;
        }
        return Object.values(byRs);
    }, [staged]);

    // ---- baseProject'ten BAGIMSIZ yeni bir proje uret (yeni upload gibi) ----
    // Tum id'ler + projectHash yeniden uretilir; boylece orijinalle history /
    // selection / dedup anahtarlari CAKISMAZ ve birbirinden tamamen bagimsiz olur.
    const makeIndependentClone = (baseProject) => {
        const dup = JSON.parse(JSON.stringify(baseProject));
        const newProjectId = v4();
        const newTitle = `${baseProject.information?.title || 'Project'} (Fixed)`;

        dup.id = newProjectId;
        // Fixlenmis geometri eski sonuclari gecersiz kilar: output (binary .O0x) ve
        // output.txt KLONLANMAZ -> duplicate proje "sonucu olmayan" temiz bir proje
        // olur ve yeniden calistirilmasi gerekir. output'tan turetilen kolon bilgileri
        // de (outputColumns / riverReachColumns) sifirlanir; aksi halde "missing
        // variables in the output file" uyarisi yanlislikla cikar.
        dup.information = {...dup.information, id: newProjectId, title: newTitle,
            hasOutputFile: 'false', outputColumns: [], riverReachColumns: []};
        // Base projeye kalici bag: tekrar fix uygulandiginda AYNI fixed projeyi
        // bulup uzerine yazmak icin (duplicateId state remount'ta sifirlanir).
        dup.fixedFromId = baseProject.information?.id;
        if (dup.projection) dup.projection = {...dup.projection, id: v4()};
        dup.shapeFiles = (dup.shapeFiles || []).map((f) => ({...f, id: v4()}));
        // output (binary 'outputBin') ve output.txt ('output') dosyalarini duplicate
        // etmiyoruz; kalan dosyalara yeni id ver.
        dup.files = (dup.files || [])
            .filter((f) => f.type !== 'output' && f.type !== 'outputBin')
            .map((f) => ({...f, id: v4()}));

        dup.projectFiles = (dup.projectFiles || []).map((pf) => {
            const plan = pf.plan ? {...pf.plan, id: v4()} : pf.plan;
            const geometry = pf.geometry ? {...pf.geometry, id: v4()} : pf.geometry;
            const flow = pf.flow ? {...pf.flow, id: v4()} : pf.flow;
            // outputBinFile (eski sonuc) duplicate'e tasinmaz. output.txt'den parse
            // edilen sonuclar (profileData / mappedProfiles) de temizlenir -> output'suz
            // yuklenmis bir proje ile ayni durum (profileData: {}, mappedProfiles: []).
            const np = {...pf, id: v4(), plan, geometry, flow, outputBinFile: null,
                profileData: {}, mappedProfiles: []};
            np.projectHash = ProjectMD5({
                email: user?.data?.email || '',
                projectTitle: newTitle || '',
                flowTitle: flow?.title || '',
                flowFile: flow?.fileName || '',
                geoTitle: geometry?.title || '',
                geoFile: geometry?.fileName || '',
                planTitle: plan?.title || '',
                planFile: plan?.fileName || '',
            });
            return np;
        });

        return dup;
    };

    // ---- TEK duplicate proje uret/guncelle ----
    const upsertDuplicate = (fixedPlans) => {
        const baseProject = selection?.base?.project;
        const baseId = baseProject?.information?.id;
        if (!baseProject || !baseId) {
            message.error('Cannot build duplicate: no base project selected.');
            return;
        }

        const isSelfOverwrite = !!baseProject.fixedFromId;
        let dup;
        if (isSelfOverwrite) {
            // Base ZATEN bir fixed proje -> yeni duplicate olusturma, KENDINI ez.
            // (Ayni information.id ile dispatch -> processProject yerinde gunceller.)
            dup = JSON.parse(JSON.stringify(baseProject));
        } else {
            // Orijinal proje: ona ait fixed projeyi kalici bag (fixedFromId) ile bul;
            // yoksa yeni bagimsiz duplicate uret.
            const existing = appContext?.projects?.find((p) => p.fixedFromId === baseId);
            dup = existing ? JSON.parse(JSON.stringify(existing)) : makeIndependentClone(baseProject);
        }
        setDuplicateId(dup.information.id);

        // Fixlenmis geometriyi dogru plana yaz (plan fileName eslesmesi) +
        // etkilenen plan id'lerini topla.
        const affectedPlanIds = new Set();
        for (const fp of fixedPlans || []) {
            const planFile = fp?.plan?.fileName || fp?.plan?.name;
            const target = (dup.projectFiles || []).find((pf) => (pf?.plan?.fileName || pf?.plan?.name) === planFile);
            if (!target) continue;
            // Geometri-seviyesi fix'ler (ST01/ST06) geometry text'i, plan-seviyesi
            // fix'ler (PS00) plan text'i degistirir; ikisini de yaz.
            if (fp?.geometry?.text) {
                target.geometry = {...target.geometry, text: fp.geometry.text};
                if (target.id) affectedPlanIds.add(target.id);
            }
            if (fp?.plan?.text) {
                target.plan = {...target.plan, text: fp.plan.text};
                if (target.id) affectedPlanIds.add(target.id);
            }
        }

        dispatchProject({type: AppActions.SET_PROJECT, data: {project: dup}});

        // SADECE kendi uzerine yazildiysa: geometri degisti -> bu projenin fixlenen
        // planlarina ait eski check sonuclari bayat. Ilgili history kayitlarini temizle.
        // Yeni bir duplicate uretildiyse (orijinalden) history aynen kalir.
        if (isSelfOverwrite && affectedPlanIds.size > 0) {
            const history = appContext?.history || [];
            const cleaned = history.filter(
                (h) => !(h?.selection?.base?.projectId === dup.information.id
                    && affectedPlanIds.has(h?.selection?.base?.planId))
            );
            if (cleaned.length !== history.length) {
                dispatchProject({type: AppActions.SET_HISTORY, data: {history: cleaned}});
            }
        }
        setSelfApplied(isSelfOverwrite);
        return dup;
    };

    // ---- GLOBAL APPLY: tum sekmelerdeki staged secimi tek seferde uygula ----
    const applyAll = async () => {
        if (!selection?.base?.plan) {
            message.warning('Select a plan (with a Check result) first.');
            return;
        }
        if (staged.length === 0) {
            message.info('Nothing selected to fix. Select items in the fix tabs first.');
            return;
        }
        const byFix = {};
        for (const {def, row} of staged) {
            if (!byFix[def.id]) byFix[def.id] = {def, targets: []};
            byFix[def.id].targets.push(def.toTarget(row));
        }
        const fixes = Object.values(byFix).map(({def, targets}) => ({fix: def.backendId, targets}));

        setLoading(true);
        setStatusText(`Applying ${staged.length} fix(es) for this plan...`);
        try {
            const payload = {
                project: getInputData(selection.base).project,
                settings: user.settings,
                plans: [{...getInputData(selection.base), fixes}],
            };
            const res = await ApiService.RasCheck.ApplyFix(payload);
            if (!res?.success) throw new Error('ApplyFix failed');
            const out = JSON.parse(res.data);
            const n = out?.changes?.length || 0;
            upsertDuplicate(out?.plans || []);
            setChanges(out?.changes || []);
            if (n === 0) {
                message.warning(`Backend reported 0 changes for ${staged.length} selected item(s). The duplicate matches the original — verify the selected structures/plan.`);
            } else {
                message.success(`Applied ${n} change(s). Duplicate project updated.`);
            }
        } catch (e) {
            console.error(e);
            message.error('Failed to apply fixes.');
        } finally {
            setLoading(false);
            setStatusText('');
        }
    };

    // ---- BATCH (bagimsiz): mevcut TUM Check sonuclarinda, TUM fix tiplerini uygula ----
    const batchApply = async () => {
        setLoading(true);
        try {
            const plans = [];
            let totalTargets = 0;
            for (const hist of appContext?.history || []) {
                if (hist.type !== 'Check') continue;
                const resolved = resolveHistorySelection(hist);
                const side = resolved?.base;
                if (!side?.plan) continue;

                const fixes = [];
                for (const def of FIXES) {
                    const scan = scanHistoryForFix(hist, def);
                    if (!scan) continue;
                    const targets = scan.items
                        .filter(def.isFixable)
                        .map((it) => def.toTarget({raw: it, params: def.defaultParams(settings, it)}));
                    if (targets.length > 0) {
                        fixes.push({fix: def.backendId, targets});
                        totalTargets += targets.length;
                    }
                }
                if (fixes.length > 0) plans.push({...getInputData(side), fixes});
            }

            if (plans.length === 0) {
                message.info('No fixable findings in existing Check results.');
                return;
            }

            setStatusText(`Applying ${totalTargets} fix(es) across ${plans.length} plan(s)...`);
            const payload = {project: getInputData(selection.base).project, settings: user.settings, plans};
            const res = await ApiService.RasCheck.ApplyFix(payload);
            if (!res?.success) throw new Error('ApplyFix failed');
            const out = JSON.parse(res.data);
            upsertDuplicate(out?.plans || []);
            setChanges(out?.changes || []);
            message.success(`Batch fix applied to ${totalTargets} item(s).`);
        } catch (e) {
            console.error(e);
            message.error('Batch fix failed.');
        } finally {
            setLoading(false);
            setStatusText('');
        }
    };

    // ---- aktif fix sekmesinin tablosu ----
    const activeDef = activeKey === 'overview' ? null : getFix(activeKey);
    const activeRows = (activeDef && fixData[activeKey]?.rows) || [];
    const activeSelected = (activeDef && fixData[activeKey]?.selected) || [];

    const fixColumns = useMemo(() => {
        if (!activeDef) return [];
        const cols = activeDef.columns.map((col) => {
            if (col.param) {
                return {
                    title: <Tooltip title={col.title}>{col.title}</Tooltip>, width: col.width, key: col.key,
                    render: (_, row) => {
                        // pierOnly kolonlar yalnizca pier'i olan koprude aktif (orn. Pier Shape K).
                        if (col.pierOnly && !row.raw?.has_pier) return <span style={{color: '#bbb'}}>—</span>;
                        if (col.param.type === 'select') {
                            return (
                                <Select size="small" style={{width: (col.width || 160) - 16}} allowClear
                                        value={row.params[col.key]}
                                        options={col.param.options.map((o) => ({value: o.value, label: o.label}))}
                                        onChange={(v) => {
                                            updateParam(activeDef.id, row.key, col.key, v);
                                            // Sekil secimi bagli sayisal alani (Cd / K) doldurur; manuel override yine mumkun.
                                            const opt = col.param.options.find((o) => o.value === v);
                                            if (opt && col.param.link) updateParam(activeDef.id, row.key, col.param.link, opt.derived);
                                        }}/>
                            );
                        }
                        return (
                            <InputNumber size="small" min={col.param.min} step={col.param.step}
                                         value={row.params[col.key]} style={{width: 70}}
                                         onChange={(v) => updateParam(activeDef.id, row.key, col.key, v)}/>
                        );
                    },
                };
            }
            return {title: col.title, width: col.width, key: col.key, render: (_, row) => col.render(row)};
        });
        cols.push({
            title: 'Status', width: 80, key: '__status',
            render: (_, row) => row.fixable ? <Tag color="red">Fixable</Tag> : <Tag color="green">OK</Tag>,
        });
        return cols;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeDef]);

    const overviewColumns = [
        {title: 'RS', dataIndex: 'rs', width: 90},
        {title: 'Type', dataIndex: 'type', width: 90},
        {
            title: 'Fixes to apply', key: 'fixes',
            render: (_, row) => row.defs.map((d) => (
                <Tag key={d.id} color="blue" style={{marginBottom: 2}}>{d.error_id} — {d.label}</Tag>
            )),
        },
    ];

    // ---- sol menu ----
    const menuItems = [
        {key: 'overview', icon: <UnorderedListOutlined/>, label: `Overview (${stagedByRs.length})`},
        ...(available.length > 0 ? [{type: 'divider'}] : []),
        ...available.map((a) => {
            const fd = fixData[a.def.id];
            const sel = fd ? fd.selected.length : a.fixableCount;
            return {key: a.def.id, icon: <ToolOutlined/>, label: `${a.def.error_id} (${sel})`};
        }),
    ];

    const renderOverview = () => (
        <>
            <Alert
                type="info"
                showIcon
                style={{marginBottom: 12}}
                message="Overview — fixes that will be applied per cross-section"
                description="This is the preview of the global Apply: every selected fix across all tabs, grouped by cross-section. Use the fix tabs on the left to review parameters and toggle items. The top Apply button applies them all at once into a single duplicate project."
            />
            {stagedByRs.length > 0 ? (
                <Table size="small" rowKey="key" columns={overviewColumns} dataSource={stagedByRs} pagination={false}/>
            ) : (
                !loading && <Empty description={statusText || (history ? 'No fixes selected' : 'Run the Check first')}/>
            )}
        </>
    );

    const renderFixView = () => (
        <>
            {activeDef && (
                <Alert
                    type="info"
                    showIcon
                    style={{marginBottom: 12}}
                    message={`Fix: ${activeDef.label} (${activeDef.error_id})`}
                    description={`${activeDef.info} Select the items to include; the top Apply button applies all selected fixes across every tab together.`}
                />
            )}
            {activeRows.length > 0 ? (
                <Table
                    size="small"
                    rowKey="key"
                    columns={fixColumns}
                    dataSource={activeRows}
                    pagination={false}
                    rowSelection={{
                        selectedRowKeys: activeSelected,
                        onChange: (keys) => setSelected(activeDef.id, keys),
                    }}
                />
            ) : (
                !loading && <Empty description={statusText || 'No findings for this fix'}/>
            )}
        </>
    );

    return (
        <div>
            <div style={{marginBottom: 12}}>
                <Popover
                    placement="bottomLeft"
                    trigger="click"
                    content={<PopoverScrollContent maxHeight={600}><SelectPlan type="base"/></PopoverScrollContent>}
                >
                    <SelectionInfo type="base"/>
                </Popover>
            </div>

            {/* Self-overwrite + applied sonrasi: toolbar/menu/tablo gizli, sadece
                proje/plan secimi (yukarida) + applied ozeti (asagida) kalir. */}
            {!(selfApplied && changes.length > 0) && (
                <>
                    {/* GLOBAL toolbar — sekmeden bagimsiz */}
                    <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12}}>
                        <Button size="small" type="primary" icon={<CaretRightOutlined/>}
                                onClick={applyAll} disabled={loading || staged.length === 0}>
                            Apply ({staged.length})
                        </Button>
                        <Tooltip title="Independent: auto-apply ALL registered fixes to ALL existing Check results">
                            <Button size="small" icon={<ForwardOutlined/>} onClick={batchApply} disabled={loading}>
                                Batch Fix All
                            </Button>
                        </Tooltip>
                        <Spin spinning={loading} size="small"/>
                        <span style={{fontSize: 12, color: '#666'}}>{statusText}</span>
                    </div>

                    <div style={{display: 'flex', gap: 16, alignItems: 'flex-start'}}>
                        {/* SOL: dikey menu (Overview + fix tipleri) */}
                        <div style={{width: 220, flexShrink: 0}}>
                            <Menu
                                mode="inline"
                                selectedKeys={[activeKey]}
                                style={{borderInlineEnd: '1px solid #f0f0f0'}}
                                onClick={({key}) => setActiveKey(key)}
                                items={menuItems}
                            />
                        </div>

                        {/* SAG: icerik */}
                        <div style={{flex: 1, minWidth: 0}}>
                            {activeKey === 'overview' ? renderOverview() : renderFixView()}
                        </div>
                    </div>
                </>
            )}

            {changes.length > 0 && (
                <>
                    <Divider/>
                    <div style={{fontSize: 12}}>
                        <b>Applied changes ({changes.length}):</b>
                        <ul style={{marginTop: 4}}>
                            {changes.map((c, i) => (
                                <li key={i}>
                                    [{c.fix}]{' '}
                                    {c.field ? (
                                        <>{c.label}: {String(c.old)} → {String(c.new)}</>
                                    ) : (
                                        <>
                                            {c.cross_section} {c.bridge_number != null ? `#${c.bridge_number}` : ''} {c.side ? `[${c.side}]` : ''}
                                            {c.xs ? ` @ XS ${c.xs}` : ''}{c.target_ratio != null ? ` → ratio ${c.target_ratio}` : ''}
                                            {c.left_old != null && ` | L: ${c.left_old}→${c.left_new}`}
                                            {c.right_old != null && ` | R: ${c.right_old}→${c.right_new}`}
                                            {c.coef_drag_new != null && ` | Cd: ${c.coef_drag_old ?? '-'}→${c.coef_drag_new}`}
                                            {c.pier_shape_k_new != null && ` | K: ${c.pier_shape_k_old ?? '-'}→${c.pier_shape_k_new}`}
                                            {c.submergence_new != null && ` | MaxSub: ${c.submergence_old ?? '-'}→${c.submergence_new}`}
                                            {c.cont_new != null && ` | Cont: ${c.cont_old ?? '-'}→${c.cont_new}`}
                                            {c.exp_new != null && ` | Exp: ${c.exp_old ?? '-'}→${c.exp_new}`}
                                            {c.skew_new != null && ` | Skew: ${c.skew_old ?? '-'}→${c.skew_new}`}
                                            {c.note ? ` (${c.note})` : ''}
                                        </>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                </>
            )}
        </div>
    );
}
